import { RecordListScroll } from '@liza/ui/components/RecordListScroll';
import { useTRPC } from '@/utils/trpc';
import { getLabResultCategory, type LabResultCategoryKey } from '@liza/fhir-utils';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import { AddLabResultForm, type AddLabResultFormValues } from '@liza/ui/components/records/AddLabResultForm';
import { LabResultCategoryList } from '@liza/ui/components/records/LabResultCategoryList';
import {
  type RecordsListFilters,
  defaultFilters,
  RecordsListControls,
} from '@liza/ui/components/records/RecordsListControls';
import type { ValueSetCode } from '@liza/ui/components/records/ValueSetCombobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@liza/ui/components/ui/dialog';
import { useCuratedLabItems } from '@liza/ui/hooks/useCuratedLabItems';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { match } from 'ts-pattern';
import { useDebouncedCallback } from 'use-debounce';
import type { DetailPanelView } from '../types';
import { parseLabLookupProps } from './labsLookup';
import { useLabResultRecords } from './useLabResultRecords';

type Patient = PatientAdminApiRouterOutputs['patient']['get']['patient'];

type Props = {
  onViewChange: (view: DetailPanelView | null) => void;
  patient: Patient;
  /** The category whose level-2 view is open, so its header reads as current. */
  selectedLabGroup?: LabResultCategoryKey;
};

type ObservationValueFields =
  | { valueQuantity: { value: number; unit: string; system: string; code: string } }
  | { valueCodeableConcept: { text: string } }
  | { valueString: string };

const buildObservationValueFields = (values: AddLabResultFormValues): ObservationValueFields =>
  match(values)
    .with({ scaleType: 'Qn' }, (v) => ({
      // FHIR `Quantity.unit` is human-readable display and `.code` is the UCUM code; they're
      // usually distinct (compare `Vitals.tsx`). Here both come from LOINC's `EXAMPLE_UCUM_UNITS`,
      // which is defined as a UCUM symbol — and the form makes the unit input read-only and
      // prop-driven (#2988), so the user can't introduce a display-only string. They coincide
      // intentionally. If a future data source surfaces both display and code separately, split
      // them at the form-values level (see `Vitals.tsx` for the pattern).
      valueQuantity: {
        value: Number(v.value),
        unit: v.unit,
        system: 'http://unitsofmeasure.org',
        code: v.unit,
      },
    }))
    .with({ scaleType: 'Ord' }, (v) => ({ valueCodeableConcept: { text: v.value } }))
    .with({ scaleType: 'Nar' }, (v) => ({ valueString: v.value }))
    .exhaustive();

export const Labs = ({ onViewChange, patient, selectedLabGroup }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<RecordsListFilters>(defaultFilters);
  const handleFiltersChange = useDebouncedCallback(setFilters, 300);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedLab, setSelectedLab] = useState<ValueSetCode | undefined>(undefined);

  const closeAddDialog = useCallback((): void => {
    setIsAddOpen(false);
    setSelectedLab(undefined);
  }, []);

  const { categories, query } = useLabResultRecords(patient.medplumId, filters);

  const labType = useCuratedLabItems();

  const lookupQuery = useQuery({
    ...trpc.codeSystem.lookup.queryOptions({
      system: selectedLab?.system ?? '',
      code: selectedLab?.code ?? '',
      properties: ['EXAMPLE_UCUM_UNITS', 'SCALE_TYP', 'ANSWER_LIST'],
    }),
    enabled: Boolean(selectedLab?.system && selectedLab.code),
  });

  const lookupProps = selectedLab
    ? parseLabLookupProps(lookupQuery.data?.properties)
    : { scaleType: 'Qn' as const, unit: undefined, ordinalChoices: undefined };

  const { mutateAsync: createObservation, isPending: isCreating } = useMutation(
    trpc.patient.observation.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.observation.list.queryKey({
            category: 'labs',
            medplumPatientId: patient.medplumId,
          }),
        });
      },
    }),
  );

  const handleCreateLabResult = useCallback(
    async (values: AddLabResultFormValues): Promise<void> => {
      try {
        await createObservation({
          resource: {
            resourceType: 'Observation',
            status: 'final',
            category: [
              {
                coding: [
                  {
                    system: 'http://terminology.hl7.org/CodeSystem/observation-category',
                    code: 'laboratory',
                  },
                ],
              },
            ],
            code: {
              coding: [
                {
                  system: values.labType.system,
                  code: values.labType.code,
                  display: values.labType.display,
                },
              ],
              text: values.labType.display ?? values.labType.code,
            },
            subject: { reference: `Patient/${patient.medplumId}` },
            effectiveDateTime: new Date(values.effectiveDateTime).toISOString(),
            issued: new Date().toISOString(),
            ...buildObservationValueFields(values),
            note: values.note ? [{ text: values.note }] : undefined,
          },
        });
        toast.success(t('labResultAdded'));
        closeAddDialog();
      } catch (error) {
        toast.error(`${t('addLabResultFailed')}: ${error instanceof Error ? error.message : ''}`);
        throw error;
      }
    },
    [closeAddDialog, createObservation, patient.medplumId, t],
  );

  return (
    <div className="flex flex-col gap-2 p-2">
      <RecordsListControls
        title={t('labs')}
        searchPlaceholder={t('searchLabs')}
        onFiltersChange={handleFiltersChange}
        onAdd={() => setIsAddOpen(true)}
        addLabel={t('addLabResult')}
      />
      <Dialog
        open={isAddOpen}
        onOpenChange={(open) => {
          if (!open) closeAddDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('addLabResult')}</DialogTitle>
          </DialogHeader>
          <AddLabResultForm
            isCreating={isCreating}
            onClose={closeAddDialog}
            onCreate={handleCreateLabResult}
            labType={labType}
            onLabTypeChange={setSelectedLab}
            scaleType={lookupProps.scaleType}
            isScaleLoading={lookupQuery.isFetching}
            unit={lookupProps.unit}
            isUnitLoading={lookupQuery.isFetching}
            ordinalChoices={lookupProps.ordinalChoices}
          />
        </DialogContent>
      </Dialog>
      <RecordListScroll
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isFetchingNextPage={query.isFetchingNextPage}
      >
        <LabResultCategoryList
          categories={categories}
          selectedCategory={selectedLabGroup}
          onSelectCategory={(labGroup) => onViewChange({ type: 'lab-group', labGroup })}
          onSelectLab={(code) =>
            onViewChange({ type: 'lab-detail', labIdentifier: code, labGroup: getLabResultCategory(code) })
          }
        />
      </RecordListScroll>
    </div>
  );
};
